import {Icon, IconButton, Table, TimeAgo} from '@wandb/components';
import React from 'react';
import {useNumberFormatter} from 'react-aria';

import {useRunFileAttachment} from '../../../aria';
import {DD_PRIVACY_HIDDEN} from '../../../util/datadog/privacy';
import {fileInfoFromName} from '../fileInfoFromName';
import {formatDownloadUrl} from '../formatDownloadUrl';
import type {
  FileBrowserFile,
  RunFileScope,
  UseFileBrowserLoadFile,
} from '../types';
import {FileRowPatchDelta} from './FileRowPatchDelta';
import {getByteUnit} from './getByteUnit';

interface FileRowProps {
  file: FileBrowserFile;
  path: string[];
  onSetFilePath(path: string[]): void;
  onDownload(file: FileBrowserFile): void;
  useLoadFile: UseFileBrowserLoadFile;
  runFileScope?: RunFileScope;
}

export function FileRow({
  file,
  path,
  onSetFilePath,
  onDownload,
  runFileScope,
  useLoadFile,
}: FileRowProps) {
  const fileName = file.name.split('/').pop() || '';
  const newPath = path.concat([fileName]);
  const attachment = useRunFileAttachment(
    runFileScope ? {...runFileScope, filePath: file.name} : undefined
  );
  const fileInfo = fileInfoFromName(fileName);
  const iconName = fileInfo.iconName;
  const {value, unit} = getByteUnit(file.sizeBytes);
  const formatter = useNumberFormatter({
    style: 'unit',
    unit,
    unitDisplay: 'narrow',
    maximumFractionDigits: 1,
  });

  return (
    <Table.Row onAction={() => onSetFilePath(newPath)}>
      <Table.Cell>
        {/* TODO: update integration tests not to rely on file-browser-file-name class */}
        <div
          className="flex items-center gap-10 p-2 cursor-pointer file-browser-file-name"
          data-test={`file-browser-file-row-${fileName}`}>
          <Icon icon={iconName} size="lg" />
          {fileName}
          <FileRowPatchDelta file={file} useLoadFile={useLoadFile} />
        </div>
      </Table.Cell>
      <Table.Cell>
        {file.updatedAt ? <TimeAgo dateTime={file.updatedAt} /> : null}
      </Table.Cell>
      <Table.Cell>{formatter.format(value)}</Table.Cell>
      <Table.Cell>
        <div
          className="flex w-full justify-end"
          data-dd-privacy={DD_PRIVACY_HIDDEN}>
          {attachment.isAvailable && (
            <IconButton
              aria-label={`Ask ARIA about ${file.name}`}
              icon="lightboard-solid"
              variant="ghost"
              isDisabled={!attachment.isEnabled}
              tooltip={{children: 'Ask ARIA about this file'}}
              onPress={attachment.onAddToChat}
            />
          )}
          <IconButton
            aria-label="Download"
            icon="download"
            variant="ghost"
            tooltip={{children: 'Download'}}
            href={formatDownloadUrl(file.url, file.name)}
            onPress={() => onDownload(file)}
          />
        </div>
      </Table.Cell>
    </Table.Row>
  );
}
